import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactFlow, ReactFlowProvider, Background, Controls, useReactFlow } from 'reactflow';
import 'reactflow/dist/style.css';
import { ChevronRight, History, Play, Plus, Save, Settings2, Loader2, X, Braces, Rocket, AlertTriangle } from 'lucide-react';
import { useTheme } from 'shared/theme/ThemeContext';
import { useBreadcrumb, useHideGlobalTopBar } from 'shared/context/BreadcrumbContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import {
  getLineage, getVersion, patchLineage, createVersion, validateGraph, previewGraph, getSchema, listIntegrations,
  previewBlastRadius, saveCanvasLayout,
} from 'features/evals3/api/evals3Api';
import EvalNode, { TriggerNode, NodeMark } from 'features/evals3/editor/EvalNode';
import NodeConfigPanel from 'features/evals3/editor/NodeConfigPanel';
import EvalSettingsPanel from 'features/evals3/editor/EvalSettingsPanel';
import { scopesFromSchemas } from 'features/evals3/editor/FilterRulesEditor';
import VersionHistoryPanel from 'features/evals3/editor/VersionHistoryPanel';
import useVersionActions from 'features/evals3/editor/useVersionActions';
import TestPanel from 'features/evals3/editor/TestPanel';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import SimpleEvalForm from 'features/evals3/editor/SimpleEvalForm';
import { isSimpleGraph } from 'features/evals3/editor/simpleModel';
import Palette from 'features/evals3/editor/Palette';
import { useCatalogModels } from 'features/evals3/editor/modelParts';
import { kindOf, ADDABLE_KINDS, NODE_KINDS } from 'features/evals3/editor/nodeKinds';
import {
  layoutGraph, edgeList, insertNode, addVerdict, removeNode, updateNode, stableKey, localProblems, hasNoSteps, NODE_W, NODE_H, V_GAP, nodeIds,
  mergeLayout, connectEdge, removeEdge, setEdges,
  displayName,
} from 'features/evals3/editor/graphModel';
import * as outcomes from 'features/evals3/editor/outcomes';
import 'features/evals3/editor/editor.css';
import { blastRadiusSentence } from 'features/evals3/blastRadiusText';
import { usePermissions, PERMISSIONS } from 'shared/hooks/usePermissions';

/**
 * One eval, open: the graph on a canvas, a step's settings beside it, the
 * versions behind it, and a test drawer underneath.
 *
 * Same shape as the Agents editor (a top bar, a fitted read-only canvas, a
 * resizable panel on the right), because that is the builder people already
 * know. What is different is on purpose: an eval's history is a list of
 * FROZEN versions, so there is no autosave — you edit a draft, and Save makes
 * the next version with a note saying what changed. Enabled is a switch on
 * the eval, not a publish step: the live version is always the newest.
 */

const NODE_TYPES = { eval: EvalNode, trigger: TriggerNode };
const TRIGGER_ID = '$trigger';
const DRAG_KIND = 'application/x-eval3-kind';
const STEP_COLOR = { ok: 'var(--feedback-success)', errored: 'var(--feedback-error)', pending: 'var(--feedback-warning)', not_ready: 'var(--feedback-warning)' };

/** Everything the editor can change that becomes part of the next version. */
const VERSIONED_KEYS = ['graph', 'applies_when', 'self_improving', 'improvement_model', 'improvement_cadence', 'backfill_policy'];

function draftOf(definition) {
  return {
    graph: definition.graph,
    applies_when: definition.applies_when ?? null,
    self_improving: Boolean(definition.self_improving),
    improvement_model: definition.improvement_model ?? null,
    improvement_cadence: definition.improvement_cadence ?? null,
    backfill_policy: definition.backfill_policy ?? { mode: 'off' },
  };
}

function Canvas({
  nodes, edges, panelOpen, focusNodeId, onNodeClick, onPaneClick, theme, layoutKey,
  readOnly = true, onNodeDragStop, onConnect, onEdgesDelete, onEdgeClick, onDropKind,
}) {
  const rf = useReactFlow();
  const wrapRef = useRef(null);
  const focus = panelOpen && focusNodeId ? nodes.find((n) => n.id === focusNodeId) : null;
  const focusX = focus ? focus.position.x : null;
  const focusY = focus ? focus.position.y : null;

  useEffect(() => {
    const fit = () => {
      if (focusX != null) rf.setCenter(focusX + NODE_W / 2, focusY + NODE_H / 2, { zoom: 0.85, duration: 220 });
      else rf.fitView({ padding: 0.3, duration: 220, maxZoom: 1 });
    };
    const t = setTimeout(fit, 30);
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return () => clearTimeout(t);
    let raf = 0;
    const ro = new ResizeObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(fit); });
    ro.observe(el);
    return () => { clearTimeout(t); cancelAnimationFrame(raf); ro.disconnect(); };
  }, [layoutKey, panelOpen, focusX, focusY, rf]);

  return (
    <div ref={wrapRef} style={{ position: 'absolute', inset: 0 }}
      onDragOver={(e) => { if (!readOnly && e.dataTransfer.types.includes(DRAG_KIND)) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
      onDrop={(e) => {
        const kind = e.dataTransfer.getData(DRAG_KIND);
        if (readOnly || !kind || !onDropKind) return;
        e.preventDefault();
        const point = { x: e.clientX, y: e.clientY };
        const flow = typeof rf.screenToFlowPosition === 'function' ? rf.screenToFlowPosition(point) : (rf.project ? rf.project(point) : point);
        onDropKind(kind, flow);
      }}>
      <ReactFlow
        style={{ background: 'var(--surface-base)' }}
        nodes={nodes} edges={edges} nodeTypes={NODE_TYPES}
        onNodeClick={onNodeClick} onPaneClick={onPaneClick}
        onNodeDragStop={onNodeDragStop} onConnect={onConnect} onEdgesDelete={onEdgesDelete} onEdgeClick={onEdgeClick}
        nodesDraggable={!readOnly} nodesConnectable={!readOnly} elementsSelectable selectNodesOnDrag={false}
        deleteKeyCode={readOnly ? null : ['Backspace', 'Delete']}
        proOptions={{ hideAttribution: true }}
        fitView fitViewOptions={{ padding: 0.35, maxZoom: 1 }}
        defaultEdgeOptions={{ type: 'smoothstep' }}
        minZoom={0.2} maxZoom={1.8} panOnDrag zoomOnScroll zoomOnPinch zoomOnDoubleClick={false} panOnScroll={false} preventScrolling
      >
        <Background variant="dots" color={theme === 'dark' ? '#444444' : '#d8d8d8'} gap={20} size={1.4} />
        <Controls showInteractive={false} position="bottom-right" />
      </ReactFlow>
    </div>
  );
}

export default function Eval3EditorPage({ teamId, lineageId, onBack, onResults, onChanged, initialPreviewVersion = null }) {
  useBreadcrumb([]);
  useHideGlobalTopBar(true);
  const { theme } = useTheme();
  const { prompt: promptDialog, confirm: confirmDialog, alert: alertDialog } = useAppDialog();

  const [data, setData] = useState(null);           // { lineage, current (the production version), versions }
  const [draft, setDraft] = useState(null);         // { graph, applies_when, self_improving, improvement_model, improvement_cadence, backfill_policy }
  // The version the draft is based on. Production by default; a saved draft
  // that is not in production keeps being edited as itself (TRO-461).
  const [baseVersion, setBaseVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openNodeId, setOpenNodeId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(null);   // a frozen definition being viewed
  const [palette, setPalette] = useState(null);                 // { from, index } | { entry: true }
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState({ errors: [], warnings: [] });
  const [nameDraft, setNameDraft] = useState('');
  const [layout, setLayout] = useState(null);       // positions a person dragged: ui.layout, never a version (TRO-449)
  const [structure, setStructure] = useState(false); // canvas | the graph as structure, same JSON
  const [mode, setMode] = useState('triage');        // simple (one check, a form) | triage (the canvas); remembered on ui.mode
  const persistTimer = useRef(null);
  const liveVersion = useRef({ version: null, ui: null });

  const [testOpen, setTestOpen] = useState(false);
  const [testUnit, setTestUnit] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const [schema, setSchema] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const modelsFor = useCatalogModels(teamId, integrations);

  // ---- load ----------------------------------------------------------------

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getLineage(teamId, lineageId);
      setData(d);
      setDraft(draftOf(d.current));
      setBaseVersion(d.current?.version ?? null);
      setLayout(d.current?.ui?.layout || null);
      liveVersion.current = { version: d.current?.version ?? null, ui: d.current?.ui || null };
      setMode(d.current?.ui?.mode === 'simple' ? 'simple' : 'triage');
      setNameDraft(d.lineage.name || '');
      setError(null);
      return d;
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
      return null;
    } finally { setLoading(false); }
  }, [teamId, lineageId]);
  useEffect(() => { load(); }, [load]);

  // Promote / restore / re-score: the same actions the results page's Versions panel runs.
  const versionActions = useVersionActions({ teamId, lineageId, target: data?.lineage?.target, reload: load, onChanged });
  const { acting, backfills, promote } = versionActions;

  /** Make a saved version the one being edited — a draft, or an old version to build on. */
  const editVersion = useCallback(async (version) => {
    const { definition } = await getVersion(teamId, lineageId, version);
    setDraft(draftOf(definition));
    setBaseVersion(definition.version);
    setLayout(definition.ui?.layout || null);
    liveVersion.current = { version: definition.version, ui: definition.ui || null };
    if (definition.ui?.mode) setMode(definition.ui.mode === 'simple' ? 'simple' : 'triage');
    setPreviewVersion(null);
    setEditedDefinition(definition);
    return definition;
  }, [teamId, lineageId]);
  const [editedDefinition, setEditedDefinition] = useState(null);

  useEffect(() => {
    if (!data?.lineage?.target) return;
    getSchema(teamId, data.lineage.target).then(setSchema).catch(() => setSchema(null));
  }, [teamId, data?.lineage?.target]);
  // The filter builder reaches into a run's spans and a conversation's runs: it needs every scope's properties.
  const [scopes, setScopes] = useState(null);
  useEffect(() => {
    let cancelled = false;
    Promise.all(['run', 'span', 'conversation'].map((t) => getSchema(teamId, t).catch(() => null)))
      .then(([run, span, conversation]) => { if (!cancelled) setScopes(scopesFromSchemas({ run, span, conversation })); });
    return () => { cancelled = true; };
  }, [teamId]);
  useEffect(() => {
    listIntegrations(teamId).then(setIntegrations).catch(() => setIntegrations([]));
  }, [teamId]);

  // ---- derived -------------------------------------------------------------

  // What the draft started from: the production version, or the version editVersion() loaded.
  const savedBase = editedDefinition && editedDefinition.version === baseVersion ? editedDefinition : data?.current;
  const saved = savedBase ? draftOf(savedBase) : null;
  const dirty = Boolean(draft && saved && stableKey(draft) !== stableKey(saved));
  const productionVersion = data?.lineage?.production_version ?? data?.current?.version ?? null;
  const latestVersion = Math.max(0, ...((data?.versions || []).map((v) => v.version)));
  const isDraft = baseVersion != null && baseVersion !== productionVersion;
  const shown = previewVersion ? draftOf(previewVersion) : draft;      // what the canvas and panels show
  const canWrite = usePermissions().can(PERMISSIONS.APP_USE);
  const readOnly = Boolean(previewVersion) || !canWrite;   // a viewer only ever looks
  const graph = shown?.graph;

  // Server-side validation, debounced, on the draft.
  useEffect(() => {
    if (!draft || readOnly) return undefined;
    let cancelled = false;
    const t = setTimeout(() => {
      validateGraph(teamId, { graph: draft.graph, applies_when: draft.applies_when })
        .then((r) => { if (!cancelled) setProblems({ errors: r.errors || [], warnings: r.warnings || [] }); })
        .catch(() => {});
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [teamId, draft, readOnly]);

  const problemByNode = useMemo(() => {
    const out = {};
    for (const p of [...localProblems(graph || {}), ...problems.errors]) if (!out[p.node_id]) out[p.node_id] = p.message;
    return out;
  }, [graph, problems]);
  const blocking = problems.errors[0] || localProblems(graph || {})[0] || null;
  const problemCount = Object.keys(problemByNode).length;

  const stepByNode = useMemo(() => {
    const out = {};
    for (const s of testResult?.steps || []) out[s.node_id] = s;
    if (testResult?.state === 'resolved' && testResult.verdict) out[testResult.verdict.node_id] = { state: 'ok', value: { verdict: testResult.verdict.passed ? 'pass' : 'fail' } };
    return out;
  }, [testResult]);

  const savedLayout = previewVersion ? previewVersion.ui?.layout : layout;
  const positions = useMemo(() => (graph ? mergeLayout(layoutGraph(graph), savedLayout) : new Map()), [graph, savedLayout]);
  // A problem with a step's routing is drawn on its lines, not only its box.
  const edgeProblem = useMemo(() => new Set(problems.errors.filter((p) => /^edges?\./.test(p.rule || '')).map((p) => p.node_id)), [problems]);
  // What the trigger scores: every unit, or the eval's sample of them.
  const samplePct = data?.lineage?.sample_pct == null ? 100 : Number(data.lineage.sample_pct);
  const targetWord = data?.lineage
    ? `${samplePct < 100 ? `${samplePct}% of ${data.lineage.target}s` : `Every ${data.lineage.target}`}${data.lineage.agent_name ? ` of ${data.lineage.agent_name}` : ''}`
    : 'Every event';
  const rfNodes = useMemo(() => {
    if (!graph) return [];
    const steps = nodeIds(graph).map((id) => {
      const node = graph.nodes[id];
      return {
        id, type: 'eval', position: positions.get(id) || { x: 0, y: 0 }, draggable: !readOnly, selected: id === openNodeId,
        sourcePosition: 'bottom', targetPosition: 'top',
        data: {
          kind: kindOf(node), node, name: node.name, isEntry: graph.entry === id, readOnly,
          invalid: Boolean(problemByNode[id]), invalidMsg: problemByNode[id], step: stepByNode[id] || null,
          outcomes: (outcomes.read(node)?.outcomes || []).map((o) => ({ key: o.key, label: o.label, to: o.to })),
        },
      };
    });
    let entryAt = graph.entry && positions.get(graph.entry);
    if (!entryAt && hasNoSteps(graph)) {
      // No first step yet: the trigger waits above the verdicts, where the first step will go.
      const xs = steps.map((n) => n.position.x); const ys = steps.map((n) => n.position.y);
      entryAt = { x: xs.length ? (Math.min(...xs) + Math.max(...xs)) / 2 : 0, y: (ys.length ? Math.min(...ys) : 0) - (NODE_H + V_GAP) };
    }
    if (!entryAt) return steps;
    return [{
      id: TRIGGER_ID, type: 'trigger', position: { x: entryAt.x, y: entryAt.y - (NODE_H + V_GAP / 2) },
      draggable: false, selectable: false, connectable: false, data: { name: targetWord },
    }, ...steps];
  }, [graph, positions, openNodeId, problemByNode, stepByNode, readOnly, targetWord]);
  const rfEdges = useMemo(() => {
    if (!graph) return [];
    const draw = ({ from, to, key, label, index }) => {
      const taken = stepByNode[from]?.edge?.to === to || stepByNode[from]?.edge_taken === to;
      const bad = edgeProblem.has(from);
      const color = taken ? STEP_COLOR[stepByNode[to]?.state] || STEP_COLOR.ok : bad ? 'var(--feedback-error)' : 'var(--border-default)';
      return {
        id: `${from}>${key ?? index}>${to}`, source: from, target: to, sourceHandle: key ?? null, type: 'smoothstep',
        label: label || undefined, labelStyle: { fontSize: 10, fill: bad ? 'var(--feedback-error)' : 'var(--text-muted)' }, labelBgStyle: { fill: 'var(--surface-base)' },
        animated: taken, style: { stroke: color, strokeWidth: taken || bad ? 2 : 1.5 },
      };
    };
    // A line per OUTCOME where the step has them, so each leaves its own dot
    // and carries that outcome's name. Steps the outcome list cannot describe
    // fall back to one line per stored edge.
    const lines = [];
    for (const id of nodeIds(graph)) {
      const node = graph.nodes[id];
      if (node.type === 'verdict') continue;
      const shape = outcomes.read(node);
      if (shape && shape.outcomes.length > 1) {
        shape.outcomes.forEach((o, i) => {
          if (o.to && graph.nodes[o.to]) lines.push(draw({ from: id, to: o.to, key: o.key, label: o.label, index: i }));
        });
      } else {
        // Several edges into one step (a decision step's routes) draw as one
        // line whose label names them all, rather than labels on top of each other.
        const byTarget = new Map();
        for (const e of edgeList(graph).filter((x) => x.from === id)) {
          const seen = byTarget.get(e.to);
          if (seen) seen.label = [seen.label, e.label].filter(Boolean).join(', ');
          else byTarget.set(e.to, { ...e });
        }
        byTarget.forEach((e) => lines.push(draw({ from: id, to: e.to, label: e.label, index: e.index })));
      }
    }
    if (!graph.entry || !graph.nodes[graph.entry]) return lines;
    return [{
      id: `${TRIGGER_ID}>${graph.entry}`, source: TRIGGER_ID, target: graph.entry, type: 'smoothstep',
      selectable: false, deletable: false, style: { stroke: 'var(--border-default)', strokeDasharray: '4 3' },
    }, ...lines];
  }, [graph, stepByNode, edgeProblem]);

  // ---- edits ----------------------------------------------------------------

  const setGraph = useCallback((next) => setDraft((d) => ({ ...d, graph: typeof next === 'function' ? next(d.graph) : next })), []);
  const changeNode = useCallback((id, patch) => setGraph((g) => updateNode(g, id, patch)), [setGraph]);

  const deleteNode = useCallback(async (id) => {
    const ok = await confirmDialog('Edges into this step will be pointed at where it would have gone next.', { title: `Delete “${graph?.nodes?.[id]?.name || id}”?`, confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try { setGraph((g) => removeNode(g, id)); setOpenNodeId(null); }
    catch (e) { alertDialog(e.message, { title: 'Cannot delete' }); }
  }, [graph, confirmDialog, alertDialog, setGraph]);

  const pickKind = useCallback((kind) => {
    const at = palette;
    setPalette(null);
    if (!at) return;
    setDraft((d) => {
      const { graph: g, id } = insertNode(d.graph, kind, at, { target: data?.lineage?.target });
      setTimeout(() => setOpenNodeId(id), 0);
      return { ...d, graph: g };
    });
  }, [palette, data?.lineage?.target]);

  const newVerdict = useCallback((nodeId, outcome, passed) => {
    setDraft((d) => {
      const { graph: g, id } = addVerdict(d.graph, { passed });
      const node = g.nodes[nodeId];
      // An outcome-shaped step (one output) routes by outcome; any other step
      // (several outputs, a decision step's routes) by edge position.
      const edges = outcomes.read(node)
        ? outcomes.route(node, outcome, id)
        : (node.edges || []).map((e, i) => (i !== outcome ? e : (e.default !== undefined ? { default: id } : { ...e, to: id })));
      return { ...d, graph: updateNode(g, nodeId, { edges }) };
    });
  }, []);
  const newStep = useCallback((nodeId, outcome) => {
    const node = graph && graph.nodes[nodeId];
    // Outcome-shaped steps insert on an outcome; any other step on that edge.
    setPalette(node && !outcomes.read(node) ? { from: nodeId, index: outcome } : { from: nodeId, outcome });
  }, [graph]);

  // A brand-new eval lands on an empty canvas, and the only thing to do there
  // is pick the first step — so ask straight away rather than making someone
  // hunt for the button. Once per eval: closing it means "not yet".
  const askedFirst = useRef(null);
  useEffect(() => {
    if (readOnly || !graph || askedFirst.current === lineageId || mode === 'simple') return;
    if (!hasNoSteps(graph)) { askedFirst.current = lineageId; return; }
    askedFirst.current = lineageId;
    setPalette({ entry: true });
  }, [graph, lineageId, readOnly, mode]);

  /** Simple or Triage: the same eval, a different editor. Remembered on the live version's ui, beside the layout. */
  const switchMode = useCallback((next) => {
    if (next === mode) return;
    setMode(next);
    setOpenNodeId(null);
    setPalette(null);
    const { version, ui } = liveVersion.current;
    const nextUi = { ...(ui || {}), mode: next };
    liveVersion.current = { version, ui: nextUi };
    if (version != null) Promise.resolve(saveCanvasLayout(teamId, lineageId, version, nextUi)).catch(() => {});
  }, [mode, teamId, lineageId]);

  // ---- canvas edits (TRO-449): the same graph, the same draft ----------------

  /** Keep a dragged position on the LIVE version's ui, in place: moving a step is never a version. */
  const placeNode = useCallback((id, point) => {
    setLayout((cur) => {
      const next = { ...(cur || {}), [id]: { x: Math.round(point.x), y: Math.round(point.y) } };
      if (persistTimer.current) clearTimeout(persistTimer.current);
      persistTimer.current = setTimeout(() => {
        const { version, ui } = liveVersion.current;
        if (version != null) saveCanvasLayout(teamId, lineageId, version, { ...(ui || {}), layout: next }).catch(() => {});
      }, 400);
      return next;
    });
  }, [teamId, lineageId]);
  useEffect(() => () => { if (persistTimer.current) clearTimeout(persistTimer.current); }, []);

  const dragStop = useCallback((_, n) => {
    if (readOnly || !n || n.id === TRIGGER_ID) return;
    placeNode(n.id, n.position);
  }, [readOnly, placeNode]);

  const connect = useCallback(({ source, sourceHandle, target }) => {
    if (readOnly || !graph || source === TRIGGER_ID) return;
    if (source === target) { alertDialog('A step cannot lead to itself.', { title: 'Cannot draw that line' }); return; }
    if (!graph.nodes[target]) return;
    // Dragged from a named outcome: that outcome now goes here, and nothing
    // else about the step's routing moves.
    const shape = sourceHandle ? outcomes.read(graph.nodes[source]) : null;
    const at = shape ? shape.outcomes.findIndex((o) => o.key === sourceHandle) : -1;
    if (at >= 0) { setGraph(setEdges(graph, source, outcomes.route(graph.nodes[source], at, target))); return; }
    const r = connectEdge(graph, source, target);
    if (r.error) { alertDialog(r.error, { title: 'Cannot draw that line' }); return; }
    setGraph(r.graph);
  }, [graph, readOnly, alertDialog, setGraph]);

  const deleteEdges = useCallback((deleted) => {
    // Only lines that were themselves selected: a key press with a step
    // selected must not strip that step's routing.
    const chosen = (deleted || []).filter((e) => e.selected && e.source !== TRIGGER_ID);
    if (readOnly || !graph || !chosen.length) return;
    // Deleting a line disconnects that outcome rather than being refused: it
    // is the obvious thing to expect from a selected line and the Delete key.
    // The step then leads nowhere, which the validator says out loud and Save
    // refuses — the rule is enforced where it can be seen, not by making the
    // keyboard not work.
    let loosened = null;
    for (const e of chosen) {
      if (!e.sourceHandle) continue;
      const shape = outcomes.read((loosened || graph).nodes[e.source]);
      const at = shape ? shape.outcomes.findIndex((o) => o.key === e.sourceHandle) : -1;
      if (at >= 0) loosened = setEdges(loosened || graph, e.source, outcomes.route((loosened || graph).nodes[e.source], at, null));
    }
    if (loosened) { setGraph(loosened); return; }
    const parsed = chosen.map((e) => { const [from, index] = e.id.split('>'); return { from, index: Number(index) }; })
      .sort((a, b) => b.index - a.index);
    let g = graph;
    for (const p of parsed) {
      const r = removeEdge(g, p.from, p.index);
      if (r.error) { alertDialog(r.error, { title: 'Cannot remove that line' }); return; }
      g = r.graph;
    }
    setGraph(g);
  }, [graph, readOnly, alertDialog, setGraph]);

  /** A kind dropped from the palette goes after the nearest step above the drop point, on its default line. */
  const dropKind = useCallback((kind, point) => {
    if (readOnly || !graph) return;
    let anchor = null; let best = Infinity;
    for (const id of nodeIds(graph)) {
      if (graph.nodes[id].type === 'verdict') continue;
      const p = positions.get(id);
      if (!p || p.y >= point.y) continue;
      const d = Math.hypot(p.x - point.x, p.y - point.y);
      if (d < best) { best = d; anchor = id; }
    }
    const index = anchor ? (graph.nodes[anchor].edges || []).findIndex((e) => e.default !== undefined) : -1;
    const at = anchor ? (index >= 0 ? { from: anchor, index } : { from: anchor }) : { entry: true };
    const { graph: g, id } = insertNode(graph, kind, at, { target: data?.lineage?.target });
    setGraph(g);
    placeNode(id, point);
    setSettingsOpen(false);
    setOpenNodeId(id);
  }, [graph, readOnly, positions, setGraph, placeNode, data?.lineage?.target]);

  useEffect(() => {
    const onAdd = (e) => {
      const id = e.detail?.nodeId;
      const node = graph?.nodes?.[id];
      if (!node || readOnly) return;
      const idx = (node.edges || []).findIndex((x) => x.default !== undefined);
      setPalette(idx >= 0 ? { from: id, index: idx } : { from: id });
    };
    window.addEventListener('eval3:add-after', onAdd);
    return () => window.removeEventListener('eval3:add-after', onAdd);
  }, [graph, readOnly]);

  // ---- lineage (no version) ---------------------------------------------------

  const patchL = useCallback(async (patch) => {
    try {
      const lineage = await patchLineage(teamId, lineageId, patch);
      setData((d) => ({ ...d, lineage }));
      onChanged?.();
    } catch (e) { alertDialog(e?.response?.data?.error || e.message, { title: 'Could not save' }); }
  }, [teamId, lineageId, onChanged, alertDialog]);

  // The name is edited in Settings; it saves itself once typing stops. (It
  // used to save on blur of a header input that no longer exists — without
  // this a rename would never leave the panel.)
  useEffect(() => {
    if (!data?.lineage) return undefined;
    const n = nameDraft.trim();
    if (!n || n === data.lineage.name) return undefined;
    const timer = setTimeout(() => patchL({ name: n }), 600);
    return () => clearTimeout(timer);
  }, [nameDraft, data?.lineage, patchL]);

  // ---- versions -----------------------------------------------------------------

  const save = useCallback(async () => {
    if (!dirty || readOnly) return;
    if (blocking) { setOpenNodeId(blocking.node_id && graph?.nodes?.[blocking.node_id] ? blocking.node_id : openNodeId); alertDialog(`${blocking.node_id ? `${blocking.node_id}: ` : ''}${blocking.message}`, { title: 'Fix this first' }); return; }
    // What the change would have done to recent verdicts, shown before the
    // person commits to it. Best effort: a failure never blocks a save.
    let impact = '';
    if (stableKey(draft.graph) !== stableKey(saved.graph)) {
      try {
        const br = await previewBlastRadius(teamId, lineageId, draft.graph);
        impact = blastRadiusSentence(br);
      } catch (_) { impact = ''; }
    }
    const note = await promptDialog(`${impact ? `${impact}\n\n` : ''}What changed, and why?`, { title: `Save as v${latestVersion + 1}`, placeholder: 'e.g. raised the judge threshold to 0.8', confirmLabel: 'Save version' });
    if (note == null) return;
    setSaving(true);
    try {
      const patch = {};
      for (const k of VERSIONED_KEYS) if (stableKey(draft[k]) !== stableKey(saved[k])) patch[k] = draft[k];
      const out = await createVersion(teamId, lineageId, { change_note: note.trim() || 'edited', based_on: baseVersion, ...patch });
      const newVersion = out?.definition?.version ?? null;
      // The picture and the mode carry over to the new version (still not part of it).
      const carried = { ...(liveVersion.current.ui || {}), ...(layout ? { layout } : {}), mode };
      if (newVersion) await Promise.resolve(saveCanvasLayout(teamId, lineageId, newVersion, carried)).catch(() => {});
      await load();
      onChanged?.();
      // Saved, not running. The label is a separate act — offered here, once,
      // while the person is looking at what they just saved.
      if (newVersion) {
        const go = await confirmDialog(
          'Nothing runs it until you do.',
          { title: `Set v${newVersion} as production?`, confirmLabel: 'Set as production', cancelLabel: 'Keep as draft' },
        );
        if (go) await promote(newVersion, { confirmed: true });
        else await editVersion(newVersion).catch(() => {});
      }
    } catch (e) {
      const d = e?.response?.data;
      if (d?.errors?.length) { setProblems({ errors: d.errors, warnings: d.warnings || [] }); alertDialog(d.errors.map((x) => `${x.node_id}: ${x.message}`).join('\n'), { title: 'The eval is not valid yet' }); }
      else alertDialog(d?.message || d?.error || e.message, { title: 'Could not save' });
    } finally { setSaving(false); }
  }, [dirty, readOnly, blocking, graph, openNodeId, promptDialog, confirmDialog, draft, saved, teamId, lineageId, load, onChanged, alertDialog, layout, baseVersion, latestVersion, editVersion, promote, mode]);

  const previewVersionN = useCallback(async (version) => {
    if (version == null) { setPreviewVersion(null); return; }
    try {
      const { definition } = await getVersion(teamId, lineageId, version);
      setPreviewVersion(definition);
      setOpenNodeId(null);
      setSettingsOpen(false);
    } catch (e) { alertDialog(e?.response?.data?.error || e.message, { title: 'Could not load that version' }); }
  }, [teamId, lineageId, alertDialog]);

  const restore = useCallback(async (version) => {
    const v = await versionActions.restore(version, { discarding: dirty });
    if (v == null) return;
    setPreviewVersion(null);
    await editVersion(v).catch(() => {});
  }, [versionActions, dirty, editVersion]);

  // Arriving from the results page's Versions panel with one version to look at.
  const previewedOnArrival = useRef(false);
  useEffect(() => {
    if (!data || initialPreviewVersion == null || previewedOnArrival.current) return;
    previewedOnArrival.current = true;
    if (initialPreviewVersion !== data.current?.version) previewVersionN(initialPreviewVersion);
  }, [data, initialPreviewVersion, previewVersionN]);

  /**
   * Leaving edit mode — to the results, or the list — with work not saved
   * asks first. Edit is a mode you step into and out of from the results
   * page now, so this is a door people walk through often.
   */
  const leave = useCallback(async (go) => {
    if (dirty) {
      const ok = await confirmDialog('Your changes to this eval have not been saved.', { title: 'Discard unsaved changes?', confirmLabel: 'Discard', danger: true });
      if (!ok) return;
    }
    go?.();
  }, [dirty, confirmDialog]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // ---- test ---------------------------------------------------------------------

  const runTest = useCallback(async (nodeId = null, mode = 'whole') => {
    if (!testUnit) { setTestOpen(true); return; }
    const target = data.lineage.target;
    setTesting(true);
    setTestOpen(true);
    try {
      const body = {
        target, [`${target}_id`]: testUnit, graph: shown.graph,
        applies_when: shown.applies_when,
      };
      if (nodeId && mode === 'only') body.only_node_id = nodeId;
      if (nodeId && mode === 'until') body.until_node_id = nodeId;
      setTestResult(await previewGraph(teamId, body));
    } catch (e) {
      const d = e?.response?.data;
      setTestResult({ state: d?.errors ? 'invalid' : 'errored', steps: [], error: d?.errors ? d.errors.map((x) => `${x.node_id}: ${x.message}`).join('\n') : (d?.message || d?.error || e.message) });
    } finally { setTesting(false); }
  }, [testUnit, data, shown, teamId]);

  useEffect(() => { setTestResult(null); }, [testUnit]);

  // ---- render --------------------------------------------------------------------

  if (loading && !data) return <PageLoader label="Loading eval" />;
  if (!data || !draft) return <div className="flex h-full items-center justify-center text-[12.5px] text-[color:var(--text-muted)]">{error || 'Eval not found.'}</div>;

  const { lineage } = data;
  const openNode = openNodeId && graph?.nodes?.[openNodeId] ? openNodeId : null;
  const panelOpen = Boolean(openNode) || settingsOpen;
  const btn = 'inline-flex h-6 items-center gap-1 rounded border border-[color:var(--border-default)] bg-surface px-2 text-[11.5px] font-medium text-[color:var(--text-primary)] hover:bg-surface-hover whitespace-nowrap transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';
  const btnOn = '!bg-surface-hover';
  const iconBtnCls = 'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-[color:var(--border-default)] bg-surface text-[color:var(--text-secondary)] hover:bg-surface-hover hover:text-[color:var(--text-primary)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-[color:var(--surface-base)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="flex h-8 shrink-0 items-center gap-1.5 border-b border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-4">
        {/* A floor, so the name truncates but never vanishes when the status and the buttons crowd the row. */}
        <nav aria-label="Breadcrumb" className="flex min-w-[11rem] shrink items-center gap-1.5">
          <button type="button" onClick={() => leave(onBack)} className="shrink-0 whitespace-nowrap text-[13px] text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]">Evals</button>
          <ChevronRight className="h-3 w-3 shrink-0 text-[color:var(--text-muted)]" aria-hidden />
          <button type="button" onClick={() => leave(onResults)} title={`${nameDraft || lineage.name}: back to the results`}
            className="min-w-0 truncate text-[13px] text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]">{nameDraft || lineage.name}</button>
          <ChevronRight className="h-3 w-3 shrink-0 text-[color:var(--text-muted)]" aria-hidden />
          <span className="shrink-0 text-[13px] font-medium text-[color:var(--text-primary)]" aria-current="page">Edit</span>
        </nav>
        <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]" data-testid="editing-version">v{baseVersion ?? data.current.version}</span>
        {!readOnly && (isDraft
          ? <span className="shrink-0 rounded bg-[color:var(--surface-sunken)] px-1.5 py-0.5 text-[10px] font-medium text-[color:var(--text-secondary)]" title={`v${productionVersion} is in production`}>draft</span>
          : <span className="shrink-0 rounded bg-success-soft px-1.5 py-0.5 text-[10px] font-medium text-success">production</span>)}
        {isDraft && !readOnly && !dirty && (
          <button type="button" onClick={() => promote(baseVersion)} disabled={acting != null} className={btn}>
            {acting === `prod:${baseVersion}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Rocket className="h-3 w-3" />} Set as production
          </button>
        )}
        {previewVersion && <span className="shrink-0 text-[11px] text-[color:var(--trodo-primary)]">Viewing v{previewVersion.version}</span>}
        {/* What blocks Save, as a count that fits the header; the words are
            on hover and on the step itself, and a click opens that step. */}
        {blocking && !readOnly && (
          <button type="button" data-testid="blocking"
            title={`${blocking.node_id !== '$graph' && blocking.node_id !== '$eval' ? `${displayName(graph, blocking.node_id)}: ` : ''}${blocking.message}`}
            aria-label={`${problemCount || 1} to fix before saving: ${blocking.message}`}
            onClick={() => {
              if (blocking.node_id === '$eval') { setSettingsOpen(true); setOpenNodeId(null); }
              else if (graph?.nodes?.[blocking.node_id]) { setOpenNodeId(blocking.node_id); setSettingsOpen(false); }
            }}
            className="inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded bg-danger-soft px-2 text-[11px] font-medium text-danger hover:opacity-90">
            <AlertTriangle className="h-3 w-3" aria-hidden /> {problemCount || 1} to fix
          </button>
        )}
        {!blocking && !readOnly && mode !== 'simple' && hasNoSteps(graph) && <span className="min-w-0 truncate text-[11px] text-[color:var(--text-muted)]" data-testid="no-steps">No steps yet. Add the first one below the trigger.</span>}
        <div className="flex-1" />
        <button type="button" title="Settings" onClick={() => { setSettingsOpen((o) => !o); setOpenNodeId(null); }} aria-label="Settings" aria-pressed={settingsOpen} className={`${iconBtnCls} ${settingsOpen ? btnOn : ''}`}><Settings2 className="h-3.5 w-3.5" aria-hidden /></button>
        {previewVersion ? (
          <button type="button" onClick={() => setPreviewVersion(null)} className={btn}><X className="h-3 w-3" /> <span className="max-lg:sr-only">Exit preview</span></button>
        ) : (
          <button type="button" title="Versions" aria-label="Versions" data-versions-trigger aria-pressed={historyOpen} onClick={() => setHistoryOpen((o) => !o)} className={`${iconBtnCls} ${historyOpen ? btnOn : ''}`}><History className="h-3.5 w-3.5" aria-hidden /></button>
        )}
        <button type="button" onClick={() => setStructure((s) => !s)} aria-label={structure ? 'Canvas' : 'Structure'} aria-pressed={structure} className={`${iconBtnCls} ${structure ? btnOn : ''}`}
          title={structure ? 'Back to the canvas' : 'The same graph, as structure'}>
          <Braces className="h-3.5 w-3.5" aria-hidden />
        </button>
        {/* Test is named, not an icon: it is the one thing here that runs the eval. Open, with a trace picked, it runs. */}
        <button type="button" title={testOpen && testUnit ? 'Run the whole eval on the picked trace' : 'Test on a real trace'}
          onClick={() => (testOpen && testUnit ? runTest() : setTestOpen((o) => !o))} disabled={testing} aria-label="Test" aria-pressed={testOpen} className={`${btn} ${testOpen ? btnOn : ''}`}>
          {testing ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Play className="h-3 w-3" aria-hidden />} Test
        </button>
        {/* Unsaved work is a dot on Save: where you act on it: rather than a
            sentence in a header that has no room for one. The words stay for
            screen readers. */}
        <button type="button" onClick={save} disabled={!dirty || saving || readOnly} title={blocking ? blocking.message : dirty ? `Save as v${latestVersion + 1}: unsaved changes` : 'Nothing to save'}
          className="relative inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded bg-brand px-2.5 text-[11.5px] font-medium text-ink-onBrand hover:bg-brand-hover disabled:opacity-50">
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
          {dirty && !readOnly && (
            <>
              <span aria-hidden className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-[color:var(--surface-base)] bg-[color:var(--feedback-warning)]" data-testid="unsaved-dot" />
              <span className="sr-only">Unsaved changes</span>
            </>
          )}
        </button>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="relative min-h-0 min-w-0 flex-1">
          {mode === 'simple' && !structure ? (
            <div className="absolute inset-0 overflow-y-auto">
              {previewVersion && <p className="mx-auto max-w-3xl px-5 pt-3 text-[12px] text-[color:var(--trodo-primary)]">Viewing v{previewVersion.version}: read only.</p>}
              <SimpleEvalForm graph={graph} onChange={(g) => setDraft((d) => ({ ...d, graph: g }))} target={lineage.target}
                schema={schema} scopes={scopes} integrations={integrations} modelsFor={modelsFor} teamId={teamId} agentName={lineage.agent_name || null} readOnly={readOnly} />
            </div>
          ) : structure ? (
            <pre data-testid="graph-structure" aria-label="The graph as structure"
              className="absolute inset-0 m-0 overflow-auto bg-[color:var(--surface-sunken)] p-4 font-mono text-[11.5px] leading-relaxed text-[color:var(--text-primary)]">
              {JSON.stringify(graph, null, 2)}
            </pre>
          ) : (
            <ReactFlowProvider>
              <Canvas nodes={rfNodes} edges={rfEdges} panelOpen={panelOpen} focusNodeId={openNode} theme={theme}
                layoutKey={`${rfNodes.length}:${rfEdges.length}`} readOnly={readOnly}
                onNodeClick={(_, n) => {
                  if (n.id === TRIGGER_ID) { setOpenNodeId(null); setSettingsOpen(true); return; }
                  setSettingsOpen(false); setOpenNodeId(n.id);
                }}
                onPaneClick={() => setOpenNodeId(null)}
                onNodeDragStop={dragStop} onConnect={connect} onEdgesDelete={deleteEdges}
                onEdgeClick={(_, e) => { if (e.source !== TRIGGER_ID) { setSettingsOpen(false); setOpenNodeId(e.source); } }}
                onDropKind={dropKind} />
            </ReactFlowProvider>
          )}
          {!readOnly && !structure && mode !== 'simple' && (
            <div className="absolute left-3 top-3 z-20 flex flex-col gap-0.5 rounded-[10px] border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] p-1 shadow-token-md" aria-label="Steps you can drag onto the canvas">
              {ADDABLE_KINDS.map((k) => (
                <div key={k} draggable role="button" tabIndex={0} title={`${NODE_KINDS[k].label}: drag onto the canvas, below the step it should follow`}
                  aria-label={`Drag ${NODE_KINDS[k].label}`}
                  onDragStart={(e) => { e.dataTransfer.setData(DRAG_KIND, k); e.dataTransfer.effectAllowed = 'move'; }}
                  className="cursor-grab rounded-md p-1 transition-colors hover:bg-surface-hover active:cursor-grabbing">
                  <NodeMark kind={k} size={24} />
                </div>
              ))}
            </div>
          )}
          {!readOnly && !structure && mode !== 'simple' && (
            hasNoSteps(graph) ? (
              /* Nothing on the canvas yet: the one thing to do belongs in the
                 middle of the empty space, not tucked into a corner. */
              <button type="button" onClick={() => setPalette({ entry: true })}
                className="absolute left-1/2 top-1/2 z-20 inline-flex h-9 -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-md border border-dashed border-[color:var(--border-strong)] bg-[color:var(--surface-base)] px-4 text-[13px] font-medium text-[color:var(--text-primary)] shadow-token-md transition-colors hover:bg-surface-hover">
                <Plus className="h-3.5 w-3.5" aria-hidden /> Add the first step
              </button>
            ) : (
              <button type="button" onClick={() => setPalette({ entry: true })} title="Insert a step before the first one"
                className="absolute bottom-5 left-1/2 z-20 inline-flex h-8 -translate-x-1/2 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] px-3 text-[12px] font-medium text-[color:var(--text-primary)] shadow-token-md transition-colors hover:bg-surface-hover">
                <Plus className="h-3.5 w-3.5" aria-hidden /> Step at the start
              </button>
            )
          )}
          {historyOpen && (
            <VersionHistoryPanel versions={data.versions} currentVersion={productionVersion} editingVersion={baseVersion} previewVersion={previewVersion?.version ?? null} dirty={dirty} acting={acting}
              onPreview={previewVersionN} onRestore={canWrite ? restore : undefined} onSetProduction={canWrite ? promote : undefined}
              backfills={backfills} onConfirmBackfill={versionActions.confirmBackfill} onCancelBackfill={versionActions.cancelBackfill}
              onClose={() => setHistoryOpen(false)} />
          )}
        </div>

        {openNode && (
          <NodeConfigPanel
            graph={graph} nodeId={openNode} readOnly={readOnly}
            onChange={changeNode} onDelete={deleteNode} onClose={() => setOpenNodeId(null)}
            onNewVerdict={newVerdict} onNewStep={newStep}
            schema={schema} scopes={scopes} target={data?.lineage?.target} integrations={integrations} modelsFor={modelsFor} teamId={teamId} agentName={data?.lineage?.agent_name || null}
            problem={problemByNode[openNode] || null} step={stepByNode[openNode] || null}
            testing={testing} onRunTest={runTest} canTest={Boolean(testUnit)}
          />
        )}
        {settingsOpen && !openNode && (
          <EvalSettingsPanel lineage={{ ...lineage, name: nameDraft }} draft={shown} readOnly={readOnly}
            onLineage={(p) => { if (p.name !== undefined) setNameDraft(p.name); else patchL(p); }}
            onDraft={(p) => setDraft((d) => ({ ...d, ...p }))}
            onClose={() => setSettingsOpen(false)} teamId={teamId} integrations={integrations} modelsFor={modelsFor} schema={schema} scopes={scopes}
            canEnable={!hasNoSteps(data.current?.graph)}
            editorMode={mode} onEditorMode={switchMode} canSimple={hasNoSteps(graph) || isSimpleGraph(graph)} />
        )}
      </div>

      {testOpen && (
        <TestPanel teamId={teamId} target={lineage.target} agentName={lineage.agent_name} graph={graph}
          unit={testUnit} onUnit={setTestUnit} result={testResult} running={testing} onRun={() => runTest()} onClose={() => setTestOpen(false)} />
      )}

      <Palette open={Boolean(palette)} onPick={pickKind} onClose={() => setPalette(null)} title={palette?.entry ? 'Add the first step' : 'Insert a step'} />
    </div>
  );
}
